import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { carePlanService } from '../services/carePlanService'
import { CarePlan } from '../types/carePlan'
import { Modal } from './Modal'
import './CarePlanCreateTemplateModal.css'

interface CarePlanCreateTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  carePlan: CarePlan
  onSuccess?: () => void
}

export function CarePlanCreateTemplateModal({
  isOpen,
  onClose,
  carePlan,
  onSuccess,
}: CarePlanCreateTemplateModalProps) {
  const queryClient = useQueryClient()
  const [templateCategory, setTemplateCategory] = useState(carePlan.templateCategory || 'general')
  const [templateName, setTemplateName] = useState(carePlan.title || '')

  const createTemplateMutation = useMutation({
    mutationFn: async () => {
      // Create template from existing care plan
      const templateData = {
        title: templateName || carePlan.title,
        description: carePlan.description,
        status: 'draft' as const,
        goals: carePlan.goals,
        interventions: carePlan.interventions,
        outcomes: carePlan.outcomes,
        isTemplate: true,
        templateCategory: templateCategory || 'general',
        // Remove patient-specific data
        patientId: undefined,
        startDate: undefined,
        endDate: undefined,
        reviewDate: undefined,
        conditionId: undefined,
        assessmentId: undefined,
        consultationId: undefined,
        transferredTo: undefined,
        transferredAt: undefined,
        transferredFrom: undefined,
        transferReason: undefined,
      }
      return carePlanService.createTemplate(templateData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-plan-templates'] })
      onSuccess?.()
      onClose()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!templateName.trim()) {
      alert('Template name is required')
      return
    }
    try {
      await createTemplateMutation.mutateAsync()
    } catch (error: any) {
      alert(error?.message || 'Failed to create template')
    }
  }

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'diabetes', label: 'Diabetes' },
    { value: 'cardiac', label: 'Cardiac' },
    { value: 'respiratory', label: 'Respiratory' },
    { value: 'mental-health', label: 'Mental Health' },
    { value: 'pediatric', label: 'Pediatric' },
    { value: 'geriatric', label: 'Geriatric' },
    { value: 'chronic-disease', label: 'Chronic Disease' },
    { value: 'preventive', label: 'Preventive' },
    { value: 'other', label: 'Other' },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Template from Care Plan" size="medium">
      <form onSubmit={handleSubmit} className="care-plan-create-template-form">
        <div className="form-group">
          <label htmlFor="templateName">
            Template Name <span className="required">*</span>
          </label>
          <input
            id="templateName"
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
            disabled={createTemplateMutation.isPending}
            placeholder="Enter template name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="templateCategory">Category</label>
          <select
            id="templateCategory"
            value={templateCategory}
            onChange={(e) => setTemplateCategory(e.target.value)}
            disabled={createTemplateMutation.isPending}
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div className="template-preview">
          <h4>Template will include:</h4>
          <ul>
            <li>
              <strong>{carePlan.goals?.length || 0}</strong> Goal{carePlan.goals?.length !== 1 ? 's' : ''}
            </li>
            <li>
              <strong>{carePlan.interventions?.length || 0}</strong> Intervention
              {carePlan.interventions?.length !== 1 ? 's' : ''}
            </li>
            <li>
              <strong>{carePlan.outcomes?.length || 0}</strong> Outcome
              {carePlan.outcomes?.length !== 1 ? 's' : ''}
            </li>
          </ul>
          <p className="template-note">
            <strong>Note:</strong> Patient-specific information (dates, patient ID, transfer data) will be removed
            from the template. The template can be reused to create new care plans for other patients.
          </p>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onClose}
            disabled={createTemplateMutation.isPending}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" disabled={createTemplateMutation.isPending} className="btn-primary">
            {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
